import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@material-ui/core';
import { Alert, AlertTitle, Color } from '@material-ui/lab';
import { DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS } from '../../utils/Constants/AlertStatusConstants';
import { useTranslation } from 'react-i18next';
import { useAlertTooltipStyles } from './AlertTooltips.styles';

interface AutoDismissAlertProps {
  message: string;
  title?: string;
  duration?: number;
  severity?: Color | string;
  onClose?: () => void;
  variant?: "filled" | "outlined" | "standard";
  closable?: boolean;
  customButtonText?: string;
  onCustomButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const AutoDismissAlert = ({
  message,
  title,
  duration = DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS,
  severity = "success",
  onClose,
  variant = "standard",
  closable = true,
  customButtonText,
  onCustomButtonClick
}: AutoDismissAlertProps) => {
  const { t } = useTranslation();
  const classes = useAlertTooltipStyles();
  const [mounted, setMounted] = useState(true);
  const [exiting, setExiting] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const exitTimerRef = useRef<NodeJS.Timeout | null>(null);
  const remainingTimeRef = useRef(duration);
  const pauseStartRef = useRef<number | null>(null);
  const showCloseButton = closable && severity === 'error';
  const severityClassName = severity === 'success'
    ? classes.successAlert
    : severity === 'info'
      ? classes.infoAlert
      : severity === 'error'
        ? classes.errorAlert
        : undefined;

  useEffect(() => {
    setMounted(true);
    setExiting(false);
    setAnimationKey(prev => prev + 1);

    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    if (severity !== 'error') {
      exitTimerRef.current = setTimeout(() => {
        startExitAnimation();
      }, duration);
    }

    return () => {
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message, duration, severity]);

  const handleMouseEnter = () => {
    if (exitTimerRef.current) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
      pauseStartRef.current = Date.now();
    }
  };

  const handleMouseLeave = () => {
    if (severity === 'error') {
      return;
    }

    if (pauseStartRef.current) {
      remainingTimeRef.current = Math.max(0, DEFAULT_DELAY_SHOW_ALERT_IN_MILLISECONDS);
      pauseStartRef.current = null;
      if (remainingTimeRef.current > 0) {
        exitTimerRef.current = setTimeout(() => {
          startExitAnimation();
        }, remainingTimeRef.current);
      } else {
        startExitAnimation();
      }
    }
  };

  const startExitAnimation = (force = false) => {
    if (severity === 'error' && !force) {
      return;
    }

    setExiting(true);
    setTimeout(() => {
      setMounted(false);
      if (onClose) onClose();
    }, 300);
  };

  const handleClose = () => {
    startExitAnimation(true);
  };

  if (!mounted) return null;

  return (
    <Box
      key={animationKey}
      className={`${classes.alertContainer} ${exiting ? classes.alertExiting : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Alert
        severity={severity as Color}
        {...(showCloseButton ? { onClose: handleClose } : {})}
        variant={variant as "filled" | "outlined" | "standard"}
        className={`${classes.alertRoot}${severityClassName ? ` ${severityClassName}` : ''}`}
        style={{ borderRadius: 8 }}
      >
        {title && <AlertTitle>{t(title)}</AlertTitle>}
        <span className={customButtonText ? classes.messageWithButton : undefined}>
          {t(message)}
          {customButtonText && (
            <button
              className={classes.customButton}
              onClick={onCustomButtonClick}
              type="button"
            >
              {customButtonText}
            </button>
          )}
        </span>
      </Alert>
    </Box >
  );
};

export default AutoDismissAlert;
